import type { RouteLocationRaw } from 'vue-router'
import {
  projectFeatures,
  supportedReviewFeatures,
  type ProjectFeatureSupport,
  type ProjectReviewFeature,
} from '@/domain/projectFeatures'

interface ProjectFeatureRoute {
  name?: unknown
  params: Record<string, unknown>
  query: Record<string, unknown>
}

function requiresProjectFeatureSettings(to: ProjectFeatureRoute): boolean {
  const name = String(to.name ?? '')
  if (name.startsWith('customer-') && typeof to.params.id === 'string') return true
  return name.startsWith('project-extraction') || name === 'project-review-workbook'
}

export function projectFeatureRouteRedirect(
  to: ProjectFeatureRoute,
  settings: Partial<ProjectFeatureSupport>,
): RouteLocationRaw | null {
  const support = projectFeatures(settings)
  const name = String(to.name ?? '')
  const id = typeof to.params.id === 'string' ? to.params.id : null
  if (!id || !requiresProjectFeatureSettings(to)) return null
  if (name === 'customer-workbooks') {
    return support.supports_workbook ? null : { name: 'customer-files', params: { id } }
  }
  if (name.startsWith('customer-')) return null
  const feature = requestedReviewFeature(to)
  const supported = supportedReviewFeatures(support)
  if (supported.includes(feature)) return null
  const fallback = supported[0]
  if (!fallback) return { name: 'project-files', params: { id } }
  if (fallback === 'workbooks') return { name: 'project-review-workbook', params: { id } }
  return { name: 'project-extractions', params: { id }, query: { type: fallback } }
}

function requestedReviewFeature(to: ProjectFeatureRoute): ProjectReviewFeature {
  const name = String(to.name ?? '')
  if (name === 'project-review-workbook') return 'workbooks'
  if (name === 'project-extraction-cert')
    return to.query.data_type === 'channels' ? 'channel' : 'assay'
  if (name.endsWith('-channel')) return 'channel'
  if (name.endsWith('-assay')) return 'assay'
  return to.query.type === 'channel' ? 'channel' : 'assay'
}

interface ProjectFeatureStore {
  featuresFor: (projectId: number) => ProjectFeatureSupport
  fetchProject: (projectId: number) => Promise<unknown>
  fetchCustomerSummary: (projectId: number) => Promise<unknown>
}

export async function projectFeatureAdmission(
  to: ProjectFeatureRoute,
  customerViewer: boolean,
  projects: ProjectFeatureStore,
): Promise<RouteLocationRaw | null> {
  if (!requiresProjectFeatureSettings(to)) return null
  const projectId = Number(to.params.id)
  try {
    if (customerViewer || String(to.name).startsWith('customer-'))
      await projects.fetchCustomerSummary(projectId)
    else await projects.fetchProject(projectId)
  } catch {
    if (String(to.name).startsWith('customer-') && to.name !== 'customer-workbooks') return null
    return customerViewer
      ? { name: 'customer-files', params: { id: String(projectId) } }
      : { name: 'projects' }
  }
  return projectFeatureRouteRedirect(to, projects.featuresFor(projectId))
}
